import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Plus, FileText } from 'lucide-react';
import TemplatePreview from '../components/TemplatePreview';

const originalTemplate = `
<html>
<head>
<meta charset="UTF-8">
<title>Original 6x9 Postcard Template</title>
<style>
  *, *:before, *:after {
    -webkit-box-sizing: border-box;
    -moz-box-sizing: border-box;
    box-sizing: border-box;
  }

  body {
    width: 9.25in;
    height: 6.25in;
    margin: 0;
    padding: 0;
    position: relative;
  }

  .watercolor-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: url({{artwork_url}});
    background-size: 50% auto;
    background-position: center;
    background-repeat: no-repeat;
    z-index: 1;
  }

  .frame-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: url({{frame_url}});
    background-size: contain;
    background-repeat: no-repeat;
    z-index: 2;
  }

  #safe-area {
    position: absolute;
    width: 8.875in;
    height: 5.875in;
    left: 0.1875in;
    top: 0.1875in;
    background-color: rgba(255,255,255,0.5);
    z-index: 3;
  }

  #ink-free {
    position: absolute;
    width: 4.2835in;
    height: 3.375in;
    right: 0.275in;
    bottom: 0.25in;
    background-color: white;
  }
</style>
</head>
<body>
  <div class="watercolor-overlay"></div>
  <div class="frame-overlay"></div>
  <div id="safe-area"></div>
  <div id="ink-free"></div>
</body>
</html>`;

const Templates: React.FC = () => {
  const [selectedTemplate, setSelectedTemplate] = useState<'reference' | 'design'>('reference');
  const [showCode, setShowCode] = useState(false);
  const [showSafeZone, setShowSafeZone] = useState(true);
  const [showInkFree, setShowInkFree] = useState(true);
  const [previewUrls, setPreviewUrls] = useState({
    frame_url: 'https://qrmzfkvnksglemsiaxnp.supabase.co/storage/v1/object/public/Templates//Wood_frame_template_w_text.png',
    artwork_url: 'https://qrmzfkvnksglemsiaxnp.supabase.co/storage/v1/object/public/Templates//Berg_Watercolor.png'
  });

  const getTemplateHtml = (baseHtml: string) => {
    let modifiedHtml = baseHtml;
    
    // Replace merge variables with actual URLs for preview
    modifiedHtml = modifiedHtml
      .replace(/{{frame_url}}/g, previewUrls.frame_url)
      .replace(/{{artwork_url}}/g, previewUrls.artwork_url);
    
    if (!showSafeZone) {
      modifiedHtml = modifiedHtml.replace(
        'background-color: rgba(255,255,255,0.5);',
        'background-color: transparent;'
      );
    }
    
    if (!showInkFree) {
      modifiedHtml = modifiedHtml.replace(
        'background-color: white;',
        'display: none;'
      );
    }
    
    return modifiedHtml;
  };

  const frontTemplate = `
<html>
<head>
<meta charset="UTF-8">
<link href="https://fonts.googleapis.com/css?family=Open+Sans" rel="stylesheet" type="text/css">
<title>Lob.com Sample 4x6 Postcard Front</title>
<style>
  *, *:before, *:after {
    -webkit-box-sizing: border-box;
    -moz-box-sizing: border-box;
    box-sizing: border-box;
  }

  body {
    width: 6.25in;
    height: 4.25in;
    margin: 0;
    padding: 0;
    /* If using an image, the background image should have dimensions of 1875x1275 pixels. */
    background-image: url(https://s3-us-west-2.amazonaws.com/public.lob.com/assets/beach.jpg);
    background-size: 6.25in 4.25in;
    background-repeat: no-repeat;
  }

  #safe-area {
    position: absolute;
    width: 5.875in;
    height: 3.875in;
    left: 0.1875in;
    top: 0.1875in;
    background-color: rgba(255,255,255,0.5);
  }

  .text {
    margin: 10px;
    font-family: 'Open Sans';
    font-weight: 400;
    font-size: 40px;
    color: white;
    text-shadow: 2px 2px black;
  }
</style>
</head>

<body>
  <div id="safe-area">
    <!-- All text should appear within the safe area. -->
    <div class="text">
      The frosted white box is the safe area. Do not put text outside this box.
    </div>
  </div>
</body>

</html>`;

  const backTemplate = `
<html>
<head>
<meta charset="UTF-8">
<link href="https://fonts.googleapis.com/css?family=Open+Sans" rel="stylesheet" type="text/css">
<title>Lob.com Sample 4x6 Postcard Back</title>
<style>
  *, *:before, *:after {
    -webkit-box-sizing: border-box;
    -moz-box-sizing: border-box;
    box-sizing: border-box;
  }

  body {
    position: relative;
    width: 6.25in;
    height: 4.25in;
    margin: 0;
    padding: 0;
    /* If using an image, the background image should have dimensions of 1875x1275 pixels. */
    background-image: url(https://s3-us-west-2.amazonaws.com/public.lob.com/assets/beach.jpg);
    background-size: 6.25in 4.25in;
    background-repeat: no-repeat;
  }

  #safe-area {
    position: absolute;
    width: 5.875in;
    height: 3.875in;
    left: 0.1875in;
    top: 0.1875in;
    background-color: rgba(255,255,255,0.5);
  }

  #ink-free {
    position: absolute;
    width: 3.2835in;
    height: 2.375in;
    right: 0.275in;
    bottom: 0.25in;
    background-color: white;
  }

  .text {
    margin: 10px;
    width: 200px;
    font-family: 'Open Sans';
    font-weight: 400;
    font-size: 18px;
    color: white;
    text-shadow: 2px 2px black;
  }
</style>
</head>

<body>
  <div id="safe-area">
    <!-- All text should appear within the safe area. -->
    <div class="text">
      The frosted white box denotes the safe area to place text. The solid white box denotes the ink-free area which must be left blank. If you are using the merge_variables argument, you can add variables like this: {{variable_name}}.
    </div>
  </div>

  <!-- Delete this div before submitting your postcard! -->
  <!-- Do not place any artwork or text in the ink free area. Address and postage will be automatically printed here by Lob. -->
  <div id="ink-free"></div>
</body>

</html>`;

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Link
              to="/"
              className="text-gray-500 hover:text-gray-700 transition-colors"
            >
              <ArrowLeft className="w-6 h-6" />
            </Link>
            <h1 className="text-2xl font-semibold text-gray-900">
              Marketing Templates
            </h1>
            <div className="flex bg-gray-100 p-1 rounded-lg ml-4">
              <button
                onClick={() => setSelectedTemplate('reference')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  selectedTemplate === 'reference'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Reference
              </button>
              <button
                onClick={() => setSelectedTemplate('design')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  selectedTemplate === 'design'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Design
              </button>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm text-gray-600">
              <input
                type="checkbox"
                checked={showSafeZone}
                onChange={(e) => setShowSafeZone(e.target.checked)}
                className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
              Show safe zone
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-600">
              <input
                type="checkbox"
                checked={showInkFree}
                onChange={(e) => setShowInkFree(e.target.checked)}
                className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
              Show ink-free zone
            </label>
            <button
              onClick={() => setShowCode(!showCode)}
              className="inline-flex items-center gap-2 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
            >
              <FileText className="w-4 h-4" />
              {showCode ? 'Hide Code' : 'Show Code'}
            </button>
            <button
              className="inline-flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Create Template
            </button>
          </div>
        </div>

        <div className="space-y-6">
          {/* Reference Template */}
          <div className={`bg-white rounded-xl shadow-sm p-6 space-y-6 ${selectedTemplate === 'design' ? 'opacity-50' : ''}`}>
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-medium text-gray-900">
                Reference Template
              </h2>
              <div className="flex gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Frame URL
                  </label>
                  <input
                    type="url"
                    value={previewUrls.frame_url}
                    onChange={(e) => setPreviewUrls(prev => ({ ...prev, frame_url: e.target.value }))}
                    className="w-96 px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="Enter frame image URL"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Artwork URL
                  </label>
                  <input
                    type="url"
                    value={previewUrls.artwork_url}
                    onChange={(e) => setPreviewUrls(prev => ({ ...prev, artwork_url: e.target.value }))}
                    className="w-96 px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="Enter artwork image URL"
                  />
                </div>
              </div>
              {showCode && (
                <div className="flex gap-2">
                  <button className="px-3 py-1 text-xs bg-indigo-600 text-white rounded hover:bg-indigo-700 transition-colors">
                    Copy
                  </button>
                  <button className="px-3 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 transition-colors">
                    Download
                  </button>
                </div>
              )}
            </div>
            <TemplatePreview html={getTemplateHtml(originalTemplate)} />
            {showCode && (
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">HTML Template</h3>
                <pre className="bg-gray-900 text-gray-100 p-6 rounded-lg overflow-x-auto text-sm">
                  <code>{originalTemplate}</code>
                </pre>
              </div>
            )}
          </div>

          {/* Design Template */}
          <div className={`bg-white rounded-xl shadow-sm p-6 space-y-6 ${selectedTemplate === 'reference' ? 'opacity-50' : ''}`}>
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-medium text-gray-900">
                Design Template
              </h2>
              {showCode && (
                <div className="flex gap-2">
                  <button className="px-3 py-1 text-xs bg-indigo-600 text-white rounded hover:bg-indigo-700 transition-colors">
                    Copy
                  </button>
                  <button className="px-3 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 transition-colors">
                    Download
                  </button>
                </div>
              )}
            </div>
            <TemplatePreview html={getTemplateHtml(frontTemplate)} />
            {showCode && (
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">HTML Template</h3>
                <pre className="bg-gray-900 text-gray-100 p-6 rounded-lg overflow-x-auto text-sm">
                  <code>{frontTemplate}</code>
                </pre>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Templates;